import { memo, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Button,
  Dropdown,
  DateField as DSDateField,
  IconArrowDropDown,
  IconArrowLeft,
  TextField
} from '@machobear/design-system'
import addDays from 'date-fns/addDays'
import addYears from 'date-fns/addYears'
import DFformat from 'date-fns/format'
import { useField } from 'formik'

import * as styles from './style.css'

type OptionPair = {
  value: string
  label: string
}

type Props = {
  name: string
  options?: Array<OptionPair>
  label?: string
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  required?: boolean
}

const DateDropdownField = ({
  name,
  label = '',
  disabled = false,
  required = true,
  minDate = addDays(new Date(), 1),
  maxDate = addYears(new Date(), 1)
}: Props) => {
  const { t } = useTranslation()

  const MoveInDateOptions = useMemo(
    () => [
      {
        value: 'asap',
        label: t('As soon as possible')
      },
      {
        value: 'next_30_days',
        label: t('In the next 30 days')
      },
      {
        value: 'next_90_days',
        label: t('In the next 3 months')
      },
      {
        value: 'custom',
        label: t('Custom date')
      }
    ],
    [t]
  )
  const [displayValue, setDisplayValue] = useState<string>(
    MoveInDateOptions[0].label
  )
  const [presetDate, setPresetDate] = useState<string>('asap')
  const [customDate, setCustomDate] = useState<Date | null>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)
  const [, { value, error, touched }, { setValue: setFieldValue }] =
    useField(name)

  const presetDateRef = useRef<{
    clearValue: () => void
    focus: () => void
  } | null>(null)
  const customDateRef = useRef<{ focus: () => void; close: () => void } | null>(
    null
  )

  const resetPresetDate = () => {
    if (['asap', 'next_30_days', 'next_90_days'].includes(value)) {
      setPresetDate(value)
    } else {
      setPresetDate('')
      presetDateRef.current?.clearValue()
    }
  }

  const onBack = () => {
    setIsCalendarOpen(false)
    customDateRef.current?.close()
    resetPresetDate()
    presetDateRef.current?.focus()
  }

  const onFocusout = () => {
    setIsCalendarOpen(false)
    customDateRef.current?.close()
    resetPresetDate()
  }

  return (
    <>
      <Box position="relative" className={styles.DateDropdownField}>
        <Box
          position="absolute"
          inset={0}
          style={{
            zIndex: 2, // above textfield
            visibility: isCalendarOpen ? 'visible' : 'hidden',
            paddingTop: 1 // alignment adjust
          }}
        >
          <Button
            size="large"
            variant="ghost"
            icon={<IconArrowLeft />}
            onClick={onBack}
          >
            Back
          </Button>
        </Box>
        <Box
          position="absolute"
          inset={0}
          style={{ zIndex: 1 }}
          onClick={() => {
            presetDateRef.current?.focus()
          }}
        >
          <TextField
            id={name}
            name={name}
            value={displayValue}
            onChange={() => {}}
            onBlur={() => {}}
            label={label}
            required={required}
            disabled={disabled}
            message={error && touched ? error : null}
            tone={error && touched ? 'critical' : 'neutral'}
            type={'text'}
            placeholder={label}
          />
          <Box position="absolute" style={{ right: 15, top: 20 }}>
            <IconArrowDropDown />
          </Box>
        </Box>
        <Box position="absolute" inset={0} style={{ visibility: 'hidden' }}>
          <Dropdown
            name={name + '_preset'}
            id={name + '_preset'}
            label={label}
            value={presetDate}
            onChange={val => {
              setPresetDate(val ?? '')

              if (val === 'custom') {
                setIsCalendarOpen(true)
                customDateRef.current?.focus()
              } else {
                const [{ label }] = MoveInDateOptions.filter(
                  op => op.value === val
                )
                setCustomDate(null)
                setDisplayValue(label)
                setFieldValue(val, true)
              }
            }}
            options={MoveInDateOptions}
            isMulti={false}
            ref={presetDateRef}
          />
        </Box>
        <Box position="absolute" inset={0} style={{ visibility: 'hidden' }}>
          <DSDateField
            label={label}
            name={name + '_custom'}
            id={name + '_custom'}
            value={customDate}
            minDate={minDate}
            maxDate={maxDate}
            onChange={val => {
              setCustomDate(val)
              setIsCalendarOpen(false)

              const dateStr = DFformat(val!, 'yyyy-MM-dd')
              setDisplayValue(dateStr)
              setFieldValue(dateStr, true)

              setPresetDate('')
              presetDateRef.current?.clearValue()
            }}
            onBlur={() => {}}
            onClose={onFocusout}
            ref={customDateRef}
          />
        </Box>
      </Box>
    </>
  )
}

export default memo(DateDropdownField)
